// Shared pieces of the business portal (FR-40 … FR-45): queries, badges, the
// shown-once secret panel and the confirmation dialog (now in shared/ui).
import { Alert, Badge, Button, Code, CopyButton, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { unwrap } from "../../shared/session/errors";

export { ConfirmDialog } from "../../shared/ui/ConfirmDialog";

export type Company = Schemas["Company"];
export type MyCompany = Schemas["MyCompany"];
export type StaffRole = Schemas["StaffRole"];
export type Segment = Schemas["NewCompany"]["segment"];

export const SEGMENTS: Segment[] = [
  "bank",
  "sacco",
  "fintech",
  "telco",
  "retail",
  "logistics",
  "government",
  "ngo",
  "other",
];
export const ROLES: StaffRole[] = ["company_admin", "company_viewer"];

export const MINE_KEY = ["business", "mine"] as const;
export const companyKey = (id: string, ...rest: string[]) => ["business", id, ...rest] as const;

/** useMyCompanies lists the companies the signed-in person is staff of, with their role. */
export function useMyCompanies() {
  return useQuery({ queryKey: MINE_KEY, queryFn: () => unwrap(api.GET("/companies/mine")) });
}

const STATUS_COLOUR: Record<Company["status"], string> = { pending: "yellow", active: "green", suspended: "red" };

export function CompanyStatusBadge({ status }: { status: Company["status"] }) {
  const { t } = useTranslation();
  return (
    <Badge color={STATUS_COLOUR[status]} data-testid="company-status">
      {t(`company_status.${status}`)}
    </Badge>
  );
}

export function RoleBadge({ role }: { role: StaffRole }) {
  const { t } = useTranslation();
  return <Badge variant="default">{t(`staff_role.${role}`)}</Badge>;
}

export function EnvironmentBadge({ sandbox }: { sandbox: boolean }) {
  const { t } = useTranslation();
  return (
    <Badge color={sandbox ? "grape" : "blue"} variant="light">
      {sandbox ? t("business.sandbox") : t("business.live")}
    </Badge>
  );
}

export type SecretValue = { key: string; label: string; value: string };

/**
 * SecretOnce shows a newly issued secret exactly once. It lives only in the
 * caller's component state (never the query cache) and is dropped on "Done".
 */
export function SecretOnce({ title, values, onDone }: { title: string; values: SecretValue[]; onDone: () => void }) {
  const { t } = useTranslation();
  return (
    <Paper withBorder p="md" radius="md" data-testid="secret-once">
      <Stack gap="sm">
        <Title order={2} size="h4">
          {title}
        </Title>
        <Alert color="orange" role="alert" title={t("secret.warning_title")}>
          {t("secret.warning")}
        </Alert>
        {values.map((v) => (
          <Stack key={v.key} gap={4}>
            <Text size="sm" fw={600}>
              {v.label}
            </Text>
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <Code
                block
                data-testid={`secret-${v.key}`}
                style={{ wordBreak: "break-all", whiteSpace: "pre-wrap", flex: 1 }}
              >
                {v.value}
              </Code>
              <CopyButton value={v.value} timeout={3000}>
                {({ copied, copy }) => (
                  <Button
                    size="xs"
                    variant="light"
                    onClick={copy}
                    aria-label={copied ? t("common.copied") : t("secret.copy_label", { label: v.label })}
                  >
                    {copied ? t("common.copied") : t("common.copy")}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Stack>
        ))}
        <Button onClick={onDone} w="fit-content">
          {t("secret.done")}
        </Button>
      </Stack>
    </Paper>
  );
}

/** AdminsOnly replaces an admin tab for a company_viewer, without calling the API. */
export function AdminsOnly() {
  const { t } = useTranslation();
  return (
    <Alert color="red" role="alert" data-testid="company-admins-only">
      {t("company.admins_only")}
    </Alert>
  );
}
